/**
 * เขตปลอดภัย (safe zones, docs/GDD.md): the places of a zone where no player may strike another (PK), no one may be
 * kidnapped (systems/offlineWork.ts) and monsters give up the chase (systems/safeZones.ts). A zone listed here uses
 * these shapes in place of its layout's PK-safe outline (World `inSafeZone`); a zone not listed keeps the
 * outline. Inside a building is always safe where the building says so (interiors.ts). A bout in the duelling
 * ground (data/duelGround.ts) is fought whatever the place.
 *
 * Data only: rectangles in map metres [x0, x1, y0, y1), or circles (centre and radius, map metres; the safe-zone
 * rule of docs/research/C0_SCENE_PLAINS.md §6: every open map has at least one wide circle round its plaza).
 */
import { plains256, type ZoneId } from './zones';
import { SAFE as PLAINS_SAFE } from './layouts/plains';

export interface SafeZoneDef {
  readonly zone: ZoneId;
  readonly id: string;
  /** Thai, for the toast and the badge. */
  readonly name: string;
  readonly rect?: readonly [number, number, number, number];
  /** A circle: its centre and radius (metres). */
  readonly circle?: { readonly at: readonly [number, number]; readonly r: number };
}

/** The new Yanyu (data/layouts/yanyu2.ts, plan B): the plaza, every school's court, the harbour market. */
import { SECT_SAFE_ZONES } from './layouts/sects';
import { CITY_SAFE_ZONES } from './layouts/cities';
const Y2: ZoneId = 'yanyu2';

export const SAFE_ZONES: readonly SafeZoneDef[] = [
  ...SECT_SAFE_ZONES,
  ...CITY_SAFE_ZONES,
  ...(['town-b', 'town-c', 'town-d'] as const).map((zone) => ({ zone, id: `${zone}-plaza`, name: 'ลานกลางเมือง', circle: { at: [48.5, 52.5], r: 16 } as const })),
  { zone: Y2, id: 'y2-plaza', name: 'ลานกลางเมือง', rect: [260, 340, 260, 340] },
  { zone: Y2, id: 'y2-mist-peak', name: 'ลานสำนักยอดหมอก', rect: [80, 124, 70, 112] },
  { zone: Y2, id: 'y2-bell-stone', name: 'ลานสำนักหินระฆัง', rect: [146, 196, 74, 120] },
  { zone: Y2, id: 'y2-pine-ink', name: 'ลานหอหมึกสน', rect: [60, 110, 168, 202] },
  { zone: Y2, id: 'y2-lantern-road', name: 'ลานทางโคมเหล็ก', rect: [460, 496, 140, 190] },
  { zone: Y2, id: 'y2-dim-moon', name: 'ลานหุบเขาจันทร์มัว', rect: [516, 560, 44, 88] },
  { zone: Y2, id: 'y2-cinnabar', name: 'ลานสำนักดาบชาด', rect: [550, 590, 140, 190] },
  { zone: Y2, id: 'y2-harbour', name: 'ตลาดท่าเรือ', rect: [250, 360, 500, 552] },
];

/**
 * The plains' circles (data/layouts/plains.ts `SAFE`): the plaza, the duel ring, the sect hall, the field shrine.
 * In force only with the 256 m plains (`plains256()`); the 72 m map keeps its plaza outline (`inSafeArea`).
 */
export const PLAINS_SAFE_ZONES: readonly SafeZoneDef[] = PLAINS_SAFE.map((s) => ({
  zone: 'plains',
  id: s.id,
  name: s.name,
  // The layout's points are tiles; a circle stands on its tile's middle (the stele's tile: the plaza's middle).
  circle: { at: [s.at[0] + 0.5, s.at[1] + 0.5] as const, r: s.r },
}));

/** The safe zones in force now. */
export const activeSafeZones = (): readonly SafeZoneDef[] =>
  plains256() ? [...SAFE_ZONES, ...PLAINS_SAFE_ZONES] : SAFE_ZONES;

/** Whether a point is inside a safe zone's shape. */
export function insideSafeZone(s: SafeZoneDef, x: number, y: number): boolean {
  if (s.circle) {
    const dx = x - s.circle.at[0];
    const dy = y - s.circle.at[1];
    return dx * dx + dy * dy < s.circle.r * s.circle.r;
  }
  return !!s.rect && x >= s.rect[0] && x < s.rect[1] && y >= s.rect[2] && y < s.rect[3];
}

/** A zone's safe zones (empty: the zone keeps its layout's outline). */
export const safeZonesIn = (zone: string): SafeZoneDef[] => activeSafeZones().filter((s) => s.zone === zone);

/** The safe zone of a zone at a point, or null. */
export function safeZoneAt(zone: string, x: number, y: number): SafeZoneDef | null {
  for (const s of activeSafeZones()) if (s.zone === zone && insideSafeZone(s, x, y)) return s;
  return null;
}

/** Whether a zone draws its safety from this table. */
export const hasSafeZones = (zone: string): boolean => activeSafeZones().some((s) => s.zone === zone);
